import React from 'react';
import { intl } from 'ehs-react';
import { usePortletStore } from '../App';
import { useUserMenuStore } from '../stores/menuUser.store';
import shallow from 'zustand/shallow';
import { userToSwitchSelected } from '../utils';
import type { icModel } from 'generated-ts-common';

type MenuUser = icModel.saf.infrastructure.model.menu.MenuUser;

export type userTriggerFct = (user: MenuUser) => void;

function MenuUserView({
  user,
  userTriggered,
  validMenuPortals
}: {
  user: MenuUser;
  userTriggered: userTriggerFct;
  validMenuPortals: Array<string>;
}): JSX.Element {
  // determine if it is primary:
  const currentPath = window.location.pathname.replace('/group/', '');
  const menuMatching =
    validMenuPortals.filter(portal => currentPath.startsWith(portal)).length >
    0;
  const isPrimary: boolean = user.selected && menuMatching;

  // assemble class names:
  const classNames: string =
    'representativeRole item first dockbar-item' +
    (isPrimary ? ' primaryColor' : '');

  // determine name text:
  const nameText = user.currentUser
    ? intl('loginhook.usermenu.self.name.text')
    : user.name;

  // determine head text:
  let headText;
  if (user.currentUser) {
    if (user.portalAccess === 'PHYSICIAN') {
      headText = intl('loginhook.usermenu.self.physician.head.text');
    } else if (user.portalAccess === 'PATIENT') {
      headText = intl('loginhook.usermenu.self.patient.head.text');
    } else if (user.portalAccess === 'DEFAULT') {
      headText = intl('loginhook.usermenu.self.default.head.text');
    } else if (user.portalAccess === 'ADMIN') {
      headText = intl('loginhook.usermenu.self.admin.head.text');
    } else {
      // throw an exception if the portalAccess is undefined. The point of this is that extending the enum
      // makes it very obvious to a developer that this also needs to be updated
      throw new Error(
        `PortalAccess ${user.portalAccess} is not supported by the menu.`
      );
    }
  } else {
    headText =
      user.portalAccess === 'PHYSICIAN'
        ? intl('loginhook.usermenu.ass.head.text')
        : intl('loginhook.usermenu.rep.head.text');
  }

  return (
    <li>
      <a className={classNames} href="#" onClick={() => userTriggered(user)}>
        <span
          className={
            'bigIcon ' +
            (user.portalAccess === 'PHYSICIAN'
              ? 'ehealthicon-physician'
              : 'ehealthicon-user-normal')
          }
        />
        <div className="text">
          <span className="small">{headText}</span>
          <span className="normal">{nameText}</span>
        </div>
      </a>
    </li>
  );
}

export function MenuUsers(): JSX.Element {
  const configuration = usePortletStore(state => state.configuration);
  const [users] = useUserMenuStore(state => [state.users], shallow);

  return (
    <ul className="menuusers" hidden={users.length == 0}>
      {users &&
        users.map(p => (
          <MenuUserView
            key={p.name}
            user={p}
            userTriggered={userToSwitchSelected}
            validMenuPortals={configuration.validMenuPortals}
          />
        ))}
    </ul>
  );
}
